/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useState, useEffect } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

interface CollectionsMetadata {
  last_synced: any;
  total_count: number;
}

export default function CollectionsSyncStatus() {
  const [syncData, setSyncData] = useState<CollectionsMetadata | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const db = getFirestore();
    
    console.log('Setting up Collections sync status listener...');
    
    // Use onSnapshot for real-time updates
    const unsubscribe = onSnapshot(
      doc(db, 'shopify_metadata', 'collections'),
      (doc) => {
        console.log('Collections sync status updated!');
        console.log('Doc exists:', doc.exists());
        console.log('Doc data:', doc.data());
        
        if (doc.exists()) {
          setSyncData(doc.data() as CollectionsMetadata);
        } else {
          setSyncData(null);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to Collections sync status:', error);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, []);

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) {
      console.log('No Collections timestamp provided');
      return 'Never';
    }
    
    console.log('Formatting Collections timestamp:', timestamp);
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    console.log('Parsed Collections date:', date);
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
       
        fontSize: '.8em',
        color: '#6c757d'
      }}>
        Loading Collections sync status...
      </div>
    );
  }

  return (
    <div className="sync-status">
      <strong>Collections Last Updated:</strong> {formatTimestamp(syncData?.last_synced)}
      {syncData?.total_count !== undefined && (
        <span style={{ marginLeft: '5px' }}>({syncData.total_count} collections)</span>
      )}
    </div>
  );
}